import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiBell,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import {
  fetchAllMeters,
  fetchFieldOfficers,
  humanizeError,
  normalizeMeter,
} from '../services/api';
import logoPng from '../assets/icons/Logo.png';
import Sidebar from '../sidebar/Sidebar';
import { SIDEBAR_CONFIG } from '../sidebar/sidebar.config';
import SidebarLogout from '../sidebar/SidebarLogout';
import UserMenu from './UserMenu';
import './Layout.css';

const RESULT_LIMIT = 8;

function statusToneClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'accepted') return 'success';
  if (s === 'pending' || s === 'pending to verify') return 'warning';
  if (s === 'rejected') return 'danger';
  return 'neutral';
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [meters, setMeters] = useState([]);
  const [metersLoaded, setMetersLoaded] = useState(false);
  const [metersLoading, setMetersLoading] = useState(false);
  const [metersError, setMetersError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { query, setQuery, clearSearch } = useSearch();
  const searchRef = useRef(null);
  const searchWrapRef = useRef(null);

  const currentPage = useMemo(() => {
    const exact = SIDEBAR_CONFIG.find((item) => item.route === location.pathname);
    if (exact) return exact;
    const prefixMatches = SIDEBAR_CONFIG
      .filter(
        (item) => item.route !== '/' && location.pathname.startsWith(`${item.route}/`),
      )
      .sort((a, b) => b.route.length - a.route.length);
    return prefixMatches[0] || null;
  }, [location.pathname]);
  const pageTitle = currentPage ? currentPage.label : 'Dashboard';

  const loadMeters = useCallback(async () => {
    if (metersLoaded || metersLoading) return;
    setMetersLoading(true);
    setMetersError('');
    try {
      const [raw, officerMap] = await Promise.all([
        fetchAllMeters(),
        fetchFieldOfficers().catch(() => ({})),
      ]);
      const normalized = raw.map((m) => normalizeMeter(m, officerMap));
      setMeters(normalized);
      setMetersLoaded(true);
    } catch (err) {
      setMetersError(humanizeError(err, 'Could not load meters for search.'));
    } finally {
      setMetersLoading(false);
    }
  }, [metersLoaded, metersLoading]);

  const trimmedQuery = query.trim();

  useEffect(() => {
    if (trimmedQuery && !metersLoaded && !metersLoading) {
      loadMeters();
    }
  }, [trimmedQuery, metersLoaded, metersLoading, loadMeters]);

  const results = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return meters
      .filter((m) =>
        String(m.meter).toLowerCase().includes(q) ||
        String(m.qtr).toLowerCase().includes(q) ||
        String(m.area).toLowerCase().includes(q) ||
        String(m.depot).toLowerCase().includes(q) ||
        String(m.state).toLowerCase().includes(q) ||
        String(m.officer).toLowerCase().includes(q),
      )
      .slice(0, RESULT_LIMIT);
  }, [meters, trimmedQuery]);

  const showResultsPanel = resultsOpen && trimmedQuery.length > 0;

  useEffect(() => {
    setActiveIdx(0);
  }, [trimmedQuery]);

  useEffect(() => {
    if (!showResultsPanel) return undefined;
    const onPointer = (e) => {
      if (searchWrapRef.current?.contains(e.target)) return;
      setResultsOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [showResultsPanel]);

  const handleSelectMeter = useCallback(
    (meter) => {
      if (!meter) return;
      setResultsOpen(false);
      clearSearch();
      searchRef.current?.blur();
      navigate(`/meters-installed?meterId=${encodeURIComponent(String(meter.id))}`);
    },
    [clearSearch, navigate],
  );

  const handleSearchKey = useCallback(
    (e) => {
      if (!showResultsPanel) {
        if (e.key === 'ArrowDown' && trimmedQuery) {
          setResultsOpen(true);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((idx) => (results.length ? (idx + 1) % results.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((idx) => (results.length ? (idx - 1 + results.length) % results.length : 0));
      } else if (e.key === 'Enter') {
        const pick = results[activeIdx] || results[0];
        if (pick) {
          e.preventDefault();
          handleSelectMeter(pick);
        }
      } else if (e.key === 'Escape') {
        setResultsOpen(false);
      }
    },
    [showResultsPanel, trimmedQuery, results, activeIdx, handleSelectMeter],
  );

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      searchRef.current?.focus();
    }
    if (e.key === 'Escape' && document.activeElement === searchRef.current) {
      clearSearch();
      setResultsOpen(false);
      searchRef.current?.blur();
    }
  }, [clearSearch]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="layout">
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <img className="sidebar-logo" src={logoPng} alt="Best Infra" />
        </div>

        <div className="sidebar-card">
          <button
            className="collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>

          <div className="sidebar-body">
            <div className="sidebar-nav">
              <Sidebar collapsed={collapsed} />
            </div>
            <SidebarLogout collapsed={collapsed} />
          </div>
        </div>
      </aside>

      <div className={`main-wrapper ${collapsed ? 'expanded' : ''}`}>
        <header className="topbar">
          <h1 className="page-title">{pageTitle}</h1>
          <div
            ref={searchWrapRef}
            className={`topbar-search ${focused ? 'search-focused' : ''} ${query ? 'search-active' : ''}`}
          >
            <FiSearch className="search-icon" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search meter, qtr, area, depot, state, officer..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setResultsOpen(true);
              }}
              onFocus={() => {
                setFocused(true);
                if (trimmedQuery) setResultsOpen(true);
              }}
              onBlur={() => setFocused(false)}
              onKeyDown={handleSearchKey}
              role="combobox"
              aria-expanded={showResultsPanel}
              aria-controls="global-search-results"
              aria-autocomplete="list"
              aria-activedescendant={
                showResultsPanel && results[activeIdx]
                  ? `global-search-option-${results[activeIdx].id}`
                  : undefined
              }
            />
            {query ? (
              <button
                className="search-clear"
                onClick={() => {
                  clearSearch();
                  setResultsOpen(false);
                  searchRef.current?.focus();
                }}
                title="Clear search"
              >
                <FiX />
              </button>
            ) : (
              <kbd>Ctrl+K</kbd>
            )}

            {showResultsPanel ? (
              <div
                id="global-search-results"
                className="global-search-results"
                role="listbox"
              >
                {metersLoading && results.length === 0 ? (
                  <div className="global-search-state">Searching meters…</div>
                ) : metersError && results.length === 0 ? (
                  <div className="global-search-state global-search-state-error">
                    {metersError}
                  </div>
                ) : results.length === 0 ? (
                  <div className="global-search-state">
                    No meters match “{trimmedQuery}”.
                  </div>
                ) : (
                  <>
                    <div className="global-search-hint">Meters</div>
                    {results.map((meter, idx) => (
                      <button
                        type="button"
                        key={meter.id}
                        id={`global-search-option-${meter.id}`}
                        role="option"
                        aria-selected={idx === activeIdx}
                        className={`global-search-row${
                          idx === activeIdx ? ' is-active' : ''
                        }`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectMeter(meter)}
                      >
                        <div className="global-search-row-main">
                          <span className="global-search-row-meter">
                            #{meter.meter}
                          </span>
                          <span
                            className={`global-search-row-status global-search-row-status-${statusToneClass(
                              meter.status,
                            )}`}
                          >
                            {meter.status}
                          </span>
                        </div>
                        <div className="global-search-row-meta">
                          {[meter.qtr, meter.area, meter.depot, meter.state]
                            .filter((v) => v && v !== '—')
                            .join(' · ') || 'Location unavailable'}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            ) : null}
          </div>
          <div className="topbar-actions">
            <button className="icon-btn">
              <FiBell />
            </button>
            <UserMenu />
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
